import {
  ApolloClient,
  ApolloLink,
  CombinedGraphQLErrors,
  HttpLink,
  InMemoryCache,
  ServerError,
} from '@apollo/client';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLErrorCode } from '@this+that/constants';
import log from 'loglevel';

export interface TokenOptions {
  skipCache?: boolean;
}

export type TokenGetter = (options?: TokenOptions) => Promise<string | null>;

type ClerkWindow = Window & {
  Clerk?: {
    loaded?: boolean;
    load?: () => Promise<unknown>;
    session?: { getToken: TokenGetter } | null;
  };
};

const CLERK_POLL_DELAY_MS = 50;
const CLERK_POLL_MAX_ATTEMPTS = 20;

// Clerk's getToken is stale-while-revalidate: it hands back the cached JWT and
// refreshes in the background, so a tab that slept wakes up and sends an expired
// one. This context flag is what turns the single replay below into a blocking
// refresh instead of a second attempt with the same dead token.
export const REPLAY_WITH_FRESH_TOKEN = 'replayWithFreshToken';

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

const buildHttpLink = (apiUrl: string) =>
  new HttpLink({
    uri: apiUrl,
  });

const buildAuthLink = (getToken: TokenGetter) => {
  // A woken tab 401s on every in-flight operation at once — 58 of them in one
  // observed incident. Without sharing the mint they would each POST Clerk's
  // /tokens, which is the endpoint Clerk rate-limits.
  let inFlightRefresh: Promise<string | null> | null = null;

  const mintFreshToken = (): Promise<string | null> => {
    inFlightRefresh ??= getToken({ skipCache: true }).finally(() => {
      inFlightRefresh = null;
    });

    return inFlightRefresh;
  };

  const readToken = async (replaying: boolean): Promise<string | null> => {
    if (!replaying) {
      return getToken();
    }

    try {
      return await mintFreshToken();
    } catch (error) {
      // SetContextLink routes a rejecting setter straight to observer.error, so
      // letting this through would replace the auth error the caller needs to
      // see with a Clerk transport error. Reusing the cached token makes the
      // server answer with the original one instead.
      log.error(`Token refresh failed: ${(error as Error).message}`);

      return getToken().catch(() => null);
    }
  };

  return new SetContextLink(async (prevContext) => {
    const token = await readToken(
      prevContext[REPLAY_WITH_FRESH_TOKEN] === true,
    );

    return {
      headers: {
        ...prevContext['headers'],
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });
};

// UNAUTHENTICATED only — the API emits it from its Apollo context function
// alone. UNAUTHORIZED and FORBIDDEN come out of resolvers (teamStore's
// ensureUserHasFeatures, for one), by which point the operation has already
// run, so replaying one would be a wasted round trip at best and a double
// write at worst.
const isRejectedToken = (error: unknown): boolean =>
  CombinedGraphQLErrors.is(error) &&
  error.errors.some(
    (graphQLError) =>
      graphQLError.extensions?.['code'] === GraphQLErrorCode.UNAUTHENTICATED,
  );

const logLinkError = (error: unknown): void => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message }) =>
      log.error(`GraphQL error: ${message}`),
    );
  } else if (ServerError.is(error)) {
    log.error(`Server error: ${error.message}`);
  } else if (error) {
    log.error(`Other error: ${(error as Error).message}`);
  }
};

const buildErrorLink = () =>
  new ErrorLink(({ error, operation, forward }) => {
    logLinkError(error);

    // ErrorLink subscribes the replay straight to the observer instead of
    // re-entering this handler, so the flag can only already be set by a caller
    // that pre-seeded the context — this bounds that case, not a re-entry.
    if (
      !isRejectedToken(error) ||
      operation.getContext()[REPLAY_WITH_FRESH_TOKEN] === true
    ) {
      return;
    }

    // Safe for mutations too: the context function runs before any resolver, so
    // an operation carrying UNAUTHENTICATED executed nothing server-side and the
    // replay cannot double-apply a write.
    operation.setContext({ [REPLAY_WITH_FRESH_TOKEN]: true });

    return forward(operation);
  });

const buildCache = () =>
  new InMemoryCache({
    possibleTypes: {
      KnowledgeAuthor: ['KnowledgeUserAuthor', 'KnowledgeAgentAuthor'],
      TeamContactCopyResult: ['TeamContactCopied', 'PersonEmailConflict'],
      CreatePersonForTeamResult: [
        'PersonCreatedWithTeamCopy',
        'PersonCreatedTeamCopyConflict',
      ],
      CreateInviteResult: ['TeamInviteSucceeded', 'TeamInviteFailed'],
    },
    typePolicies: {
      // role varies per space; normalizing by userId would clobber it across spaces
      KnowledgeSpaceParticipant: {
        keyFields: false,
      },
      Team: {
        keyFields: ['id', 'userId'],
      },
      FlowExecution: {
        keyFields: ['executionId'],
      },
      TeamInvite: {
        keyFields: ['inviteeEmail', 'teamId'],
      },
      UserCommunication: {
        keyFields: ['userId', 'messageId'],
      },
      InstagramConversation: {
        keyFields: ['igAccountId', 'conversationId'],
      },
      FacebookConversation: {
        keyFields: ['pageId', 'conversationId'],
      },
      Board: {
        keyFields: ['userId', 'id'],
      },
      BoardColumn: {
        keyFields: ['boardId', 'id'],
      },
      BoardTag: {
        keyFields: ['boardId', 'id'],
      },
      // The id derives from the PR's HMAC, so the same PR on two tickets shares an id while
      // sourceRunId/createdAt differ per ticket; the artifact is stored inline in its entry.
      TicketArtifact: {
        keyFields: false,
      },
      // No `id` field at all — without this the row is stored inline in the
      // getMyLocalDevices result and every cache.modify by device identity no-ops.
      LocalDevice: {
        keyFields: ['userId', 'deviceId'],
      },
    },
  });

export const createApolloClient = (getToken: TokenGetter, apiUrl: string) => {
  const httpLink = buildHttpLink(apiUrl);
  const authLink = buildAuthLink(getToken);
  const errorLink = buildErrorLink();

  return new ApolloClient({
    // errorLink sits above authLink so its replay re-runs the auth link and
    // picks up the refreshed token.
    link: ApolloLink.from([errorLink, authLink, httpLink]),
    cache: buildCache(),
    devtools: {
      enabled: false,
    },
    queryDeduplication: true,
    defaultOptions: {
      query: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
      watchQuery: {
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      },
    },
  });
};

// Waits for the window.Clerk global before reading the session token to avoid
// a boot-window race where isSignedIn flips true before Clerk.session resolves.
export const getUserToken = async (
  options?: TokenOptions,
): Promise<string | null> => {
  const clerkWindow = window as ClerkWindow;

  for (let attempt = 0; attempt < CLERK_POLL_MAX_ATTEMPTS; attempt++) {
    if (clerkWindow.Clerk) {
      break;
    }
    await wait(CLERK_POLL_DELAY_MS);
  }

  const clerk = clerkWindow.Clerk;
  if (!clerk) {
    return null;
  }

  if (!clerk.loaded && clerk.load) {
    await clerk.load();
  }

  if (clerk.session) {
    return await clerk.session.getToken(options);
  }

  return null;
};
